import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import {
  PlaceDetails,
  PlaceSuggestion,
  PlacesMode,
  PlacesService,
} from '../../../core/services/places.service';

/**
 * Wiederverwendbares Adress-Autocomplete für Formulare.
 *
 * Modi:
 *  - 'address' (default): vollständige Straßenadressen mit Hausnummer.
 *    Beim Auswählen kommt ein PlaceDetails mit Strasse / Hausnummer / PLZ /
 *    Stadt / Lat / Lng zurück, das die Eltern-Komponente für Autofill nutzen
 *    kann.
 *  - 'place': Städte / PLZ / Regionen (Orts-Such-Felder im Hero).
 *
 * Session-Token-Lebenszyklus:
 *   Pro "Eingabevorgang" (Fokus rein → Auswahl/Blur) wird genau EIN Token
 *   verwendet. Suggest+Retrieve mit gleichem Token = 1 Mapbox-Billing.
 *   Nach Auswahl oder bewusstem Verwerfen → neuer Token beim nächsten Tippen.
 */
@Component({
  selector: 'app-address-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="addr-ac" [class.is-open]="isOpen">
      <input
        #input
        type="text"
        [class]="inputClass"
        [value]="value"
        [placeholder]="placeholder"
        [attr.id]="inputId || null"
        [attr.aria-label]="ariaLabel || placeholder"
        [attr.autocomplete]="autocompleteAttr"
        [disabled]="disabled"
        [attr.inputmode]="inputmode || null"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        [attr.aria-expanded]="isOpen"
        [attr.aria-activedescendant]="activeId"
        (input)="onInput($any($event.target).value)"
        (focus)="onFocus()"
        (keydown)="onKeydown($event)"
      />

      @if (isOpen && suggestions.length > 0) {
        <ul class="addr-ac__list" role="listbox">
          @for (s of suggestions; track s.mapboxId; let i = $index) {
            <li
              class="addr-ac__item"
              [class.is-active]="i === activeIndex"
              [id]="optionId(i)"
              role="option"
              [attr.aria-selected]="i === activeIndex"
              (mousedown)="select(s)"
              (mouseenter)="activeIndex = i"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round"
                   stroke-linejoin="round" aria-hidden="true">
                <path d="M12 22s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12Z"/>
                <circle cx="12" cy="10" r="2.5"/>
              </svg>
              <span class="addr-ac__item-text">
                <span class="addr-ac__item-name">{{ s.name }}</span>
                @if (s.placeFormatted && s.placeFormatted !== s.name) {
                  <span class="addr-ac__item-sub">{{ s.placeFormatted }}</span>
                }
              </span>
            </li>
          }
        </ul>
      }
    </div>
  `,
  styles: [`
    :host { display: block; position: relative; }
    .addr-ac { position: relative; }
    .addr-ac__list {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      z-index: 100;
      list-style: none;
      margin: 0;
      padding: 4px;
      background: var(--cr-surface, #fff);
      border: 1px solid var(--cr-stone-200, #e5e7eb);
      border-radius: 12px;
      box-shadow: 0 12px 32px -8px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.06);
      max-height: 320px;
      overflow-y: auto;
    }
    .addr-ac__item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 9px 12px;
      border-radius: 8px;
      cursor: pointer;
      color: var(--cr-stone-800, #1f2937);
      font-size: 14px;
      line-height: 1.35;
      transition: background-color 0.12s;
    }
    .addr-ac__item.is-active,
    .addr-ac__item:hover {
      background: var(--cr-stone-100, #f3f4f6);
    }
    .addr-ac__item > svg {
      flex: 0 0 auto;
      margin-top: 3px;
      color: var(--cr-stone-500, #6b7280);
    }
    .addr-ac__item-text {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .addr-ac__item-name { font-weight: 500; }
    .addr-ac__item-sub {
      font-size: 12.5px;
      color: var(--cr-stone-500, #6b7280);
      margin-top: 2px;
    }
  `],
})
export class AddressAutocompleteComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Eingabetext. Wir behandeln das Property semi-controlled:
   *  - bei normalen Form-Patches übernehmen wir den neuen Wert,
   *  - direkt nach einer User-Auswahl ignorieren wir genau EINE externe
   *    Änderung. Hintergrund: wir zeigen nach Pick "Marktstraße 17a" im Feld
   *    an, das Parent-Formular speichert aber nur "Marktstraße" im strasse-
   *    FormControl und re-bindet uns dadurch. Der einmalige Lock verhindert,
   *    dass dieser Re-Bind unseren Anzeige-Text zerstört.
   */
  @Input() value = '';
  @Input() placeholder = '';
  @Input() ariaLabel: string | null = null;
  @Input() inputId: string | null = null;
  @Input() inputClass = 'cr-input';
  /** 'address' (default) oder 'place'. */
  @Input() mode: PlacesMode = 'address';
  @Input() disabled = false;
  /** Native autocomplete-Attribut (z.B. 'street-address'). */
  @Input() autocompleteAttr = 'off';
  @Input() inputmode: string | null = null;
  /** Optional: Bias der Vorschläge in der Nähe dieser Koordinate. */
  @Input() proximity: { lat: number; lng: number } | null = null;
  /** Mindestlänge für Suggest-Aufruf — unter dem Wert kein Request. */
  @Input() minChars = 2;

  /** Live-Eingabe (jedes Tippen). Eltern-Komponente bekommt den Text. */
  @Output() valueChange = new EventEmitter<string>();
  /** User hat eine Suggestion ausgewählt — alle Komponenten + Koordinaten dabei. */
  @Output() addressSelected = new EventEmitter<PlaceDetails>();
  /** User hat das Feld geleert — Eltern-Komponente sollte abhängige Felder zurücksetzen. */
  @Output() selectionCleared = new EventEmitter<void>();

  @ViewChild('input', { static: true }) inputRef!: ElementRef<HTMLInputElement>;

  private readonly placesService = inject(PlacesService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly destroy$ = new Subject<void>();
  private readonly query$ = new Subject<string>();

  /** Aktiver Mapbox-Session-Token. Wird nach jeder Auswahl rotiert. */
  private sessionToken = '';

  /**
   * Skip-Flag für genau einen folgenden `value`-Input-Re-Bind nach einem Pick.
   * Siehe Doku am `value`-Input.
   */
  private skipNextValueBinding = false;

  suggestions: PlaceSuggestion[] = [];
  isOpen = false;
  activeIndex = -1;

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.sessionToken = this.placesService.newSessionToken();

    this.query$
      .pipe(
        debounceTime(220),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.trim().length < this.minChars) return of<PlaceSuggestion[]>([]);
          return this.placesService.suggest(
            q,
            this.mode,
            this.sessionToken,
            this.proximity ?? undefined,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((items) => {
        this.suggestions = items;
        this.isOpen = items.length > 0;
        this.activeIndex = items.length > 0 ? 0 : -1;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['value'];
    if (!change || change.firstChange) return;
    if (this.skipNextValueBinding) {
      this.skipNextValueBinding = false;
      return;
    }
    // Externe Quelle setzt den Wert neu (z.B. Form-Reset, Edit-Modus laden).
    // Internen Anzeige-State synchron halten — sonst friert das Feld auf dem
    // alten Wert ein, sobald der Eltern-Code patcht.
    this.value = change.currentValue ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Input-Handler ────────────────────────────────────────────────────────

  onInput(value: string): void {
    this.value = value;
    this.valueChange.emit(value);
    if (!value.trim()) {
      this.suggestions = [];
      this.isOpen = false;
      this.selectionCleared.emit();
      return;
    }
    this.query$.next(value);
  }

  onFocus(): void {
    if (this.suggestions.length > 0) this.isOpen = true;
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen && event.key !== 'ArrowDown') return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.suggestions.length === 0) return;
        if (!this.isOpen) {
          this.isOpen = true;
          this.activeIndex = 0;
          return;
        }
        this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (this.suggestions.length === 0) return;
        this.activeIndex =
          (this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length;
        return;
      case 'Enter':
        if (this.activeIndex >= 0 && this.suggestions[this.activeIndex]) {
          event.preventDefault();
          this.select(this.suggestions[this.activeIndex]);
        }
        return;
      case 'Escape':
        this.isOpen = false;
        this.activeIndex = -1;
        return;
    }
  }

  /** Klick außerhalb → Dropdown schließen. */
  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (!this.isOpen) return;
    if (target instanceof Node && this.host.nativeElement.contains(target)) return;
    this.isOpen = false;
  }

  // ── Selection ────────────────────────────────────────────────────────────

  select(s: PlaceSuggestion): void {
    this.placesService.retrieve(s.mapboxId, this.sessionToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe((details) => {
        // Token nach Retrieve verbrennen — nächster Tippvorgang bekommt einen frischen.
        this.sessionToken = this.placesService.newSessionToken();
        this.isOpen = false;
        this.suggestions = [];
        this.activeIndex = -1;

        if (!details) return;

        // Anzeige: bei Adress-Mode "Straße Hausnummer" (oder nur Straße, wenn
        // Mapbox keine HN geliefert hat), bei Place-Mode den vollen Ortsnamen.
        // Das Parent-Formular speichert Straße und Hausnummer getrennt — wir
        // emitten `valueChange` hier bewusst NICHT, damit der kombinierte
        // Display-Text nicht ins reine strasse-FormControl wandert. Stattdessen
        // patcht die Eltern-Komponente die Felder im addressSelected-Handler.
        const c = details.components;
        const visible =
          this.mode === 'address' && c.strasse
            ? (c.hausnummer ? `${c.strasse} ${c.hausnummer}` : c.strasse)
            : details.name;
        this.value = visible;
        // Skip nächsten value-Re-Bind: Parent setzt gleich strasse=Straße,
        // das würde sonst "Straße HN" → "Straße" zurücksetzen.
        this.skipNextValueBinding = true;
        this.addressSelected.emit(details);
      });
  }

  optionId(i: number): string {
    return `${this.inputId ?? 'addr-ac'}-opt-${i}`;
  }

  get activeId(): string | null {
    return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
  }
}
